import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SiteFooter } from "../components/SiteFooter";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, User, ShoppingBag, DollarSign, FileCheck, Send, Star, Quote } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";
const HERO_IMG = "https://images.unsplash.com/photo-1444723121867-7a241cacace9?w=1400&q=80";

const STEPS = [
  { key: "intended_use", title: "What brings you to Green Routes?", subtitle: "Select the option that best describes your needs.", icon: User,
    options: [
      { value: "personal", label: "Personal Use", desc: "For my own consumption" },
      { value: "resale", label: "Resale", desc: "Buying to distribute or resell" },
      { value: "brand_building", label: "Brand Building", desc: "Building a product line or brand" },
      { value: "cultivator_relationship", label: "Direct Cultivator Relationship", desc: "Private access to growers \u2014 powered by our KookaMF partnership" },
    ],
  },
  { key: "quantity", title: "How much are you looking for?", subtitle: "This helps us route you to the right tier.", icon: ShoppingBag,
    options: [
      { value: "under_1oz", label: "Under 1 oz", desc: "Retail-level quantity" },
      { value: "1_10_units", label: "1 \u2013 10 units", desc: "Small wholesale" },
      { value: "10_50_units", label: "10 \u2013 50 units", desc: "Mid-level volume" },
      { value: "50_plus", label: "50+ units", desc: "Large-scale sourcing" },
    ],
  },
  { key: "budget", title: "What\u2019s your budget range?", subtitle: "This is the primary routing factor.", icon: DollarSign,
    options: [
      { value: "under_300", label: "Under $300", desc: "Retail pricing tier" },
      { value: "400_800", label: "$400 \u2013 $800", desc: "Budget tier" },
      { value: "1000_2000", label: "$1,000 \u2013 $2,000", desc: "Premium tier" },
      { value: "2200_2800", label: "$2,200 \u2013 $2,800", desc: "Boutique / small batch" },
      { value: "coming_to_la", label: "Coming to LA for sourcing trip", desc: "Full concierge experience" },
    ],
  },
  { key: "compliance", title: "Compliance requirements?", subtitle: "Let us know your documentation needs.", icon: FileCheck,
    options: [
      { value: "none", label: "No compliance needed", desc: "No documentation required" },
      { value: "basic", label: "Basic documentation", desc: "Standard paperwork" },
      { value: "full", label: "Full compliance package", desc: "Complete regulatory compliance" },
    ],
  },
];

const TESTIMONIALS = [
  { name: "Marcus T.", role: "Brand Owner, NYC", text: "Green Routes connected me with cultivators I never would have found on my own. The quality and the network are unmatched." },
  { name: "Jade L.", role: "Dispensary Buyer, Chicago", text: "Flew out to LA on the concierge package. Met three cultivators in two days. Left with contracts locked in." },
  { name: "Devon R.", role: "Private Collector", text: "The boutique access is exactly what it sounds like \u2014 exclusive. They vet both sides so everyone wins." },
];

export default function IntakeForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", phone: "", referral_source: "" });
  const [submitting, setSubmitting] = useState(false);

  const isContactStep = step === STEPS.length;
  const currentStep = STEPS[step];
  const totalSteps = STEPS.length + 1;

  const selectOption = (value) => setAnswers((prev) => ({ ...prev, [currentStep.key]: value }));
  const canProceed = isContactStep ? contact.name.trim() && contact.email.trim() && contact.phone.trim() : !!answers[currentStep?.key];
  const handleNext = () => { if (step < STEPS.length) setStep(step + 1); };
  const handleBack = () => { if (step > 0) setStep(step - 1); };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/submissions`, { ...answers, ...contact });
      const sub = res.data;
      if (sub.lane === "retail") navigate(`/retail/${sub.id}`);
      else if (sub.lane === "concierge") navigate(`/concierge/${sub.id}`);
      else navigate(`/result/${sub.id}`);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-primary)" }} data-testid="intake-form">
      {/* Hero */}
      <div className="relative" style={{ height: "clamp(200px, 35vh, 320px)" }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,50,33,0.75) 0%, rgba(15,50,33,0.9) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col justify-between px-5 sm:px-8 md:px-12 lg:px-20 py-6 md:py-8">
          <div className="flex items-center justify-between">
            <a href="/"><img src={LOGO_URL} alt="Green Routes Consulting" className="h-9 sm:h-10 md:h-12 logo-on-dark" data-testid="logo" /></a>
            <div className="flex items-center gap-4">
              <a href="/menu" className="text-xs tracking-wider uppercase font-semibold hidden sm:inline-block" style={{ color: "rgba(253,252,247,0.55)" }} data-testid="nav-menu">Menu</a>
              <a href="/order" className="text-xs tracking-wider uppercase font-semibold hidden sm:inline-block" style={{ color: "rgba(253,252,247,0.55)" }} data-testid="nav-order">Order</a>
              <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: "rgba(253,252,247,0.45)", fontFamily: "'JetBrains Mono', monospace" }}>
                {String(step + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
              </span>
            </div>
          </div>
          <div className="max-w-2xl">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-2 font-semibold" style={{ color: "rgba(253,252,247,0.4)" }}>
              Los Angeles &middot; Curated Supply &middot; Trusted Introductions
            </p>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-tight" style={{ color: "#F5F3E9", fontFamily: "'Playfair Display', serif" }}>
              Exclusive access starts here.
            </h2>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="h-[3px] w-full" style={{ background: "var(--bg-tertiary)" }}>
        <motion.div className="h-full" style={{ background: "var(--accent)" }} animate={{ width: `${((step + 1) / totalSteps) * 100}%` }} transition={{ duration: 0.4, ease: "easeOut" }} />
      </div>

      {/* Form */}
      <div className="flex-1 flex justify-center px-5 sm:px-8 md:px-12 lg:px-20 py-8 md:py-12">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            {!isContactStep ? (
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3, ease: "easeOut" }}>
                <div className="flex items-center gap-3 mb-3">
                  {currentStep.icon && <currentStep.icon size={18} strokeWidth={1.5} style={{ color: "#C2693D" }} />}
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: "var(--text-tertiary)" }}>{currentStep.key.replace(/_/g, " ")}</p>
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight mb-2" style={{ color: "var(--text-primary)" }}>{currentStep.title}</h1>
                <p className="mb-6 md:mb-8 text-sm md:text-base" style={{ color: "var(--text-secondary)" }}>{currentStep.subtitle}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentStep.options.map((opt, i) => {
                    const sel = answers[currentStep.key] === opt.value;
                    return (
                      <motion.div key={opt.value} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, duration: 0.3 }}
                        data-testid={`option-${opt.value}`} onClick={() => selectOption(opt.value)} className="cursor-pointer p-4 sm:p-5 border"
                        style={{ background: sel ? "var(--primary)" : "var(--bg-elevated, #fff)", borderColor: sel ? "var(--primary)" : "var(--border)", borderWidth: sel ? "2px" : "1px", padding: sel ? "15px" : "16px",
                          transform: sel ? "translateY(-2px)" : "none", boxShadow: sel ? "0 4px 20px rgba(15,50,33,0.15)" : "none", transition: "all 0.2s ease" }}>
                        <p className="font-medium text-sm" style={{ color: sel ? "#F5F3E9" : "var(--text-primary)" }}>{opt.label}</p>
                        <p className="text-xs mt-1" style={{ color: sel ? "rgba(245,243,233,0.6)" : "var(--text-tertiary)" }}>{opt.desc}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ) : (
              <motion.div key="contact" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3, ease: "easeOut" }}>
                <div className="flex items-center gap-3 mb-3">
                  <Send size={18} strokeWidth={1.5} style={{ color: "#C2693D" }} />
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: "var(--text-tertiary)" }}>Contact</p>
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight mb-2" style={{ color: "var(--text-primary)" }}>How do we reach you?</h1>
                <p className="mb-8 text-sm md:text-base" style={{ color: "var(--text-secondary)" }}>All fields required unless noted.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
                  {[{ key: "name", label: "Full Name", type: "text" }, { key: "email", label: "Email Address", type: "email" }, { key: "phone", label: "Phone Number", type: "tel" }, { key: "referral_source", label: "How did you hear about us? (optional)", type: "text" }].map((field, i) => (
                    <motion.div key={field.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07, duration: 0.3 }}>
                      <label className="block text-xs uppercase tracking-[0.15em] mb-2 font-semibold" style={{ color: "var(--text-tertiary)" }}>{field.label}</label>
                      <input type={field.type} data-testid={`input-${field.key}`} value={contact[field.key]}
                        onChange={(e) => setContact((prev) => ({ ...prev, [field.key]: e.target.value }))}
                        className="w-full bg-transparent border-b-2 py-3 px-0 text-base outline-none"
                        style={{ borderColor: "var(--border)", color: "var(--text-primary)", transition: "border-color 0.2s ease" }}
                        onFocus={(e) => (e.target.style.borderColor = "var(--primary)")} onBlur={(e) => (e.target.style.borderColor = "var(--border)")} />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Nav */}
          <div className="flex items-center justify-between mt-10 pt-6" style={{ borderTop: "1px solid var(--border)" }}>
            <button onClick={handleBack} disabled={step === 0} data-testid="btn-back" className="text-sm flex items-center gap-2"
              style={{ color: step === 0 ? "var(--border)" : "var(--text-secondary)", cursor: step === 0 ? "default" : "pointer", transition: "color 0.2s ease" }}>
              <ArrowLeft size={16} /> Back
            </button>
            {isContactStep ? (
              <button onClick={handleSubmit} disabled={!canProceed || submitting} data-testid="btn-submit"
                className="px-8 sm:px-10 py-3 text-sm font-semibold tracking-wide flex items-center gap-2"
                style={{ background: canProceed && !submitting ? "var(--primary)" : "var(--bg-tertiary)", color: canProceed && !submitting ? "#F5F3E9" : "var(--text-tertiary)", cursor: canProceed && !submitting ? "pointer" : "default", transition: "all 0.2s ease" }}>
                {submitting ? "Submitting..." : "Submit"} <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={handleNext} disabled={!canProceed} data-testid="btn-next"
                className="px-8 sm:px-10 py-3 text-sm font-semibold tracking-wide flex items-center gap-2"
                style={{ background: canProceed ? "var(--primary)" : "var(--bg-tertiary)", color: canProceed ? "#F5F3E9" : "var(--text-tertiary)", cursor: canProceed ? "pointer" : "default", transition: "all 0.2s ease" }}>
                Continue <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="px-5 sm:px-8 md:px-12 lg:px-20 py-12 md:py-16" style={{ background: "var(--primary)" }} data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-4 text-center font-semibold" style={{ color: "rgba(253,252,247,0.35)" }}>What Our Clients Say</p>
          <h3 className="text-xl sm:text-2xl md:text-3xl tracking-tight text-center mb-10 md:mb-14" style={{ color: "#F5F3E9", fontFamily: "'Playfair Display', serif" }}>Trusted by buyers nationwide.</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-6 md:p-8" style={{ background: "rgba(253,252,247,0.06)", border: "1px solid rgba(253,252,247,0.1)" }} data-testid={`testimonial-${i}`}>
                <Quote size={20} strokeWidth={1.5} className="mb-4" style={{ color: "#C2693D" }} />
                <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(253,252,247,0.75)" }}>{t.text}</p>
                <div className="flex items-center gap-2 mb-1">
                  {[...Array(5)].map((_, j) => <Star key={j} size={12} fill="#C2693D" stroke="none" />)}
                </div>
                <p className="text-sm font-semibold" style={{ color: "#F5F3E9" }}>{t.name}</p>
                <p className="text-xs" style={{ color: "rgba(253,252,247,0.4)" }}>{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
